import { problemIds } from './entitySchema';
import { scoreEvidenceForProblem } from './validationScore';
import { initiativeReadiness } from './productInsights';

export const FRAMEWORKS = {
  rice: { label: 'RICE', inputs: ['reach', 'impact', 'confidence', 'effort'], formula: 'Reach × Impact × Confidence ÷ Effort' },
  ice: { label: 'ICE', inputs: ['impact', 'confidence', 'ease'], formula: '(Impact × 2) × (Confidence ÷ 10) × Ease' },
};
export const INPUTS = {
  reach: { label: 'Reach', hint: 'Customers per quarter', min: 0 },
  impact: { label: 'Impact', hint: '1 minimal – 5 massive', min: 1, max: 5 },
  confidence: { label: 'Confidence %', hint: 'Starts at the evidence score', min: 0, max: 100 },
  effort: { label: 'Effort', hint: 'Person-weeks', min: 0.1 },
  ease: { label: 'Ease', hint: '1 hard – 10 trivial', min: 1, max: 10 },
};
const SEVERITY_IMPACT = { critical: 5, high: 4, medium: 3, low: 2 };
const CLOSED = ['done', 'solved', 'archived', 'cancelled'];

export const opportunityKey = o => `${o.type}:${o.record.id}`;

// Open initiatives plus open problems that no initiative addresses yet.
export function listOpportunities(data) {
  const linked = new Set(data.initiatives.flatMap(i => problemIds(i)));
  const evidenceFor = ids => Math.max(0, ...ids.map(id => scoreEvidenceForProblem(data.evidence, id).score));
  const initiatives = data.initiatives.filter(i => !CLOSED.includes(i.status)).map(record => ({
    type: 'initiatives', record, title: record.title, horizon: record.time_horizon,
    evidenceScore: evidenceFor(problemIds(record)), readiness: initiativeReadiness(record, data).score,
    aligned: data.objectives.some(o => o.id === record.okr_id), defaultImpact: Number(record.impact) || 3,
  }));
  const problems = data.problems.filter(p => !CLOSED.includes(p.status) && !linked.has(String(p.id))).map(record => ({
    type: 'problems', record, title: record.title, horizon: 'Not planned',
    evidenceScore: evidenceFor([String(record.id)]), readiness: null,
    aligned: data.objectives.some(o => o.id === record.okr_id), defaultImpact: SEVERITY_IMPACT[record.severity] || 3,
  }));
  return [...initiatives, ...problems];
}

export function defaultInputs(opportunity) {
  const saved = opportunity.record.priority_inputs || {};
  return {
    reach: saved.reach ?? '', impact: saved.impact ?? opportunity.defaultImpact,
    confidence: saved.confidence ?? opportunity.evidenceScore, effort: saved.effort ?? '', ease: saved.ease ?? '',
  };
}

// Returns null with the missing inputs rather than guessing a value.
export function priorityScore(framework, inputs) {
  const { inputs: needed } = FRAMEWORKS[framework];
  const values = Object.fromEntries(needed.map(key => [key, inputs[key] === '' || inputs[key] == null ? NaN : Number(inputs[key])]));
  const missing = needed.filter(key => !Number.isFinite(values[key]) || values[key] < INPUTS[key].min || (INPUTS[key].max !== undefined && values[key] > INPUTS[key].max));
  if (missing.length) return { score: null, missing };
  const score = framework === 'rice'
    ? values.reach * values.impact * (values.confidence / 100) / values.effort
    : values.impact * 2 * (values.confidence / 10) * values.ease;
  return { score: Math.round(score * 10) / 10, missing: [] };
}

export const evidenceLabel = score => score >= 70 ? 'Strong' : score >= 40 ? 'Moderate' : score > 0 ? 'Weak' : 'None';

// Calculated observations only; the AI explanation is requested separately.
export function compareOpportunities(items) {
  if (items.length < 2) return [];
  const notes = [];
  const byEvidence = [...items].sort((a, b) => b.evidenceScore - a.evidenceScore);
  const [strongest, next] = byEvidence;
  if (strongest.evidenceScore - next.evidenceScore >= 15) notes.push(`${strongest.title} has materially stronger evidence (${strongest.evidenceScore} vs ${next.evidenceScore}).`);
  const efforts = items.filter(i => Number(i.inputs.effort) > 0).sort((a, b) => a.inputs.effort - b.inputs.effort);
  if (efforts.length >= 2 && efforts[efforts.length - 1].inputs.effort >= efforts[0].inputs.effort * 2) notes.push(`${efforts[0].title} needs about ${Math.round(efforts[efforts.length - 1].inputs.effort / efforts[0].inputs.effort)}× less effort than ${efforts[efforts.length - 1].title}.`);
  const unaligned = items.filter(i => !i.aligned);
  if (unaligned.length && unaligned.length < items.length) notes.push(`${unaligned.map(i => i.title).join(', ')} ${unaligned.length === 1 ? 'is' : 'are'} not linked to an objective.`);
  const nowWeaker = items.filter(i => i.horizon === 'Now' && i.evidenceScore < strongest.evidenceScore - 15 && i !== strongest);
  if (strongest.horizon !== 'Now' && nowWeaker.length) notes.push(`${strongest.title} is not in Now, but has stronger evidence than ${nowWeaker.map(i => i.title).join(', ')}.`);
  if (!notes.length) notes.push('No large calculated differences. Compare impact, effort, and risk with your team.');
  return notes;
}
